import axios from "axios";
import { plainToInstance } from "class-transformer";
import { AddDirectRecordLocationResultDto, DataRetrievalFailureReason, DocumentRetrievalFailureReason, DocumentRetrievalTaskBatchDto, DocumentRetrievalTaskDownloadDto, LocationExistsDto, PatientTEFCAAuthInfoDto, RefreshDirectRecordLocationResultDto, StartResultDto, SuggestedFastenEndpointDto, } from "healthex-common/document.retrieval.dtos";
import { err } from "healthex-common/result";
import { asEnum } from "healthex-common/utils";
import { authenticatedApiConfig, generateURL, retryTooManyRequests } from "./BaseApiService";
export class DocumentRetrievalService {
    constructor(authenticationProvider, environment) {
        this.authenticationProvider = authenticationProvider;
        this.environment = environment;
    }
    async getExistingRetrieval(patientId, input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents", { patientId }), { ...input, documentSelectionComplete: false }, await authenticatedApiConfig(this.authenticationProvider));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async retrievePatientData(patientId, input) {
        return await retryTooManyRequests(async () => {
            try {
                const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/retrieve", {
                    patientId,
                }), input, await authenticatedApiConfig(this.authenticationProvider));
                return response.data
                    ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data)
                    : err(DocumentRetrievalFailureReason.GENERIC_ERROR);
            }
            catch (e) {
                return this.checkThrownError(e);
            }
        });
    }
    async getTaskBatch(patientId, batchId) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents", { patientId }), { batchId }, await authenticatedApiConfig(this.authenticationProvider));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async getTaskBatchFinalized(patientId, batchId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/finalized", { patientId, batchId }), await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateTaskBatch(patientId, batchId, input) {
        const response = await axios.put(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}`, input, await authenticatedApiConfig(this.authenticationProvider));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async getTaskBatchDownloadLink(patientId, batchId) {
        const response = await axios.get(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}/download`, await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(DocumentRetrievalTaskDownloadDto, response.data);
    }
    async getMostRecentDownloadableOrProcessingTaskBatch(patientId) {
        const response = await axios.get(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/most-recent-downloadable-or-processing`, await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(DocumentRetrievalTaskBatchDto, response.data);
    }
    async getMostRecentFinalizedTaskBatch(patientId, mustHaveRecords) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/most-recent-finalized", {
            patientId,
        }), await authenticatedApiConfig(this.authenticationProvider, { mustHaveRecords }));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async skipTEFCARetrieval(patientId, batchId) {
        try {
            const response = await axios.post(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}/tefca/skip`, {}, await authenticatedApiConfig(this.authenticationProvider));
            return response.data
                ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data)
                : err(DocumentRetrievalFailureReason.GENERIC_ERROR);
        }
        catch (e) {
            return this.checkThrownError(e);
        }
    }
    async startTEFCARetrieval(patientId, batchId) {
        try {
            const response = await axios.post(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}/tefca-retrieval`, {}, await authenticatedApiConfig(this.authenticationProvider));
            return response.data
                ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data)
                : err(DocumentRetrievalFailureReason.GENERIC_ERROR);
        }
        catch (e) {
            return this.checkThrownError(e);
        }
    }
    checkThrownError(e) {
        var _a;
        if (axios.isAxiosError(e)) {
            if ((_a = e.response) === null || _a === void 0 ? void 0 : _a.data) {
                const errorData = e.response.data;
                for (const candidate of [errorData.code, errorData.message]) {
                    if (typeof candidate === "string") {
                        const failureReason = asEnum(DocumentRetrievalFailureReason)(candidate);
                        if (failureReason)
                            return err(failureReason);
                    }
                }
            }
        }
        throw e;
    }
    async getFhirAuthorizeUrl(patientId, batchId, endpoint, redirectUrl) {
        var _a;
        const input = {
            endpoint,
            redirectUrl,
        };
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/fhir/authorize-url", {
            patientId,
            batchId,
        }), input, await authenticatedApiConfig(this.authenticationProvider));
        return (_a = response.data) !== null && _a !== void 0 ? _a : null;
    }
    async startFhirRetrieval(patientId, batchId, input) {
        var _a;
        try {
            const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/fhir/start", {
                patientId,
                batchId,
            }), input, await authenticatedApiConfig(this.authenticationProvider));
            return plainToInstance(StartResultDto, response.data);
        }
        catch (e) {
            const baseError = this.checkThrownError(e);
            const responseData = axios.isAxiosError(e) ? (_a = e.response) === null || _a === void 0 ? void 0 : _a.data : undefined;
            return err({
                reason: baseError.error,
                locationId: typeof (responseData === null || responseData === void 0 ? void 0 : responseData.locationId) === "string" ? responseData.locationId : undefined,
            });
        }
    }
    async startFastenRetrieval(patientId, batchId, input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/fasten", {
            patientId,
            batchId,
        }), input, await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(StartResultDto, response.data);
    }
    async getSuggestedFastenEndpoints(patientId, batchId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/suggested-fasten-endpoints", {
            patientId,
            batchId,
        }), await authenticatedApiConfig(this.authenticationProvider));
        return Array.isArray(response.data) ? response.data.map((item) => plainToInstance(SuggestedFastenEndpointDto, item)) : [];
    }
    async getTaskBatchDownloadPdfLink(patientId, batchId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/download-pdf", {
            patientId,
            batchId,
        }), await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(DocumentRetrievalTaskDownloadDto, response.data);
    }
    async createDocumentDelivery(patientId, input) {
        await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/document-deliveries", { patientId }), input, await authenticatedApiConfig(this.authenticationProvider));
    }
    async getPatientTEFCAAuthInfo(patientId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/tefca-auth-info", { patientId }), await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(PatientTEFCAAuthInfoDto, response.data);
    }
    async checkIfLocationExists(patientId, batchId, input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/existing-location", {
            patientId,
            batchId,
        }), input, await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(LocationExistsDto, response.data);
    }
    async refreshDirectRecordLocation(patientId, batchId, locationId) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/locations/:locationId/refresh", {
            patientId,
            batchId,
            locationId,
        }), {}, await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(RefreshDirectRecordLocationResultDto, response.data);
    }
    async addDirectRecordLocation(patientId, batchId, input) {
        return await retryTooManyRequests(async () => {
            var _a;
            try {
                const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/direct-record-location", {
                    patientId,
                    batchId,
                }), input, await authenticatedApiConfig(this.authenticationProvider));
                const data = response.data;
                if (data && typeof data === "object" && "error" in data && typeof data.error === "string") {
                    const reason = asEnum(DataRetrievalFailureReason)(data.error);
                    if (reason) {
                        return err(reason);
                    }
                }
                return data ? plainToInstance(AddDirectRecordLocationResultDto, data) : err(DataRetrievalFailureReason.GENERIC_ERROR);
            }
            catch (e) {
                if (axios.isAxiosError(e)) {
                    const errorData = (_a = e.response) === null || _a === void 0 ? void 0 : _a.data;
                    if (errorData && typeof errorData === "object" && "error" in errorData && typeof errorData.error === "string") {
                        const reason = asEnum(DataRetrievalFailureReason)(errorData.error);
                        if (reason) {
                            return err(reason);
                        }
                    }
                }
                return err(DataRetrievalFailureReason.GENERIC_ERROR);
            }
        });
    }
}
